/**
 * OnboardingModal — shown the first time a user lands on the dashboard.
 *
 * Walks through three short steps:
 *   1. What AlphaConcreto does.
 *   2. What "fierros" are and how exports consume them.
 *   3. The monthly refill + how to subscribe for unlimited.
 *
 * State is persisted in localStorage under `alphaconcreto.onboarded.v1`.
 * If the user dismisses it, it never appears again on that device. They
 * can re-trigger it from Settings (TODO link).
 */

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ArrowRight,
  CheckCircle2,
  Infinity as InfinityIcon,
  Sparkles,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { useAuthStore } from '@/stores/authStore';

const STORAGE_KEY = 'alphaconcreto.onboarded.v1';

/** Public helper — exposed so a "Ver tour de bienvenida" button in
 *  Settings can clear the flag and re-trigger the modal. */
export function resetOnboarding(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore — private mode etc. */
  }
}

export function OnboardingModal() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    let seen = false;
    try {
      seen = localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      seen = true;
    }
    if (!seen) setOpen(true);
  }, [user?.id]);

  function close() {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setOpen(false);
    setStep(0);
  }

  const firstName = user?.name?.split(' ')[0] ?? '';
  const balance = user?.tokens_balance ?? 0;

  const steps = [
    {
      eyebrow: 'Bienvenida',
      title: firstName ? `Hola, ${firstName}` : 'Bienvenido a AlphaConcreto',
      icon: <Sparkles size={22} strokeWidth={1.8} />,
      body: (
        <>
          <p>
            AlphaConcreto es tu espacio de cálculo estructural según{' '}
            <strong>Norma Peruana E.060</strong> y <strong>ACI 318-19</strong>. Diseñas columnas,
            vigas, losas, muros, zapatas y predimensionas, todo con visualización 2D/3D en vivo.
          </p>
          <p className="mt-3">
            Cuando termines un cálculo, exportas la memoria de cálculo en{' '}
            <strong>PDF profesional</strong> o <strong>Excel editable</strong>. Esa exportación es
            lo único que cuesta — el resto es ilimitado.
          </p>
        </>
      ),
    },
    {
      eyebrow: 'Tu economía',
      title: 'Qué son los "fierros"',
      icon: <FierroIcon size={22} />,
      body: (
        <>
          <p>
            Los <strong>fierros</strong> son tu saldo de exportaciones. Cada PDF o Excel consume{' '}
            <strong>2 fierros</strong>; los predimensionamientos solo <strong>1 fierro</strong>.
          </p>
          <div
            className="mt-3 flex items-center justify-between rounded-[var(--radius-md)] border p-3"
            style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
          >
            <span style={{ color: 'var(--text-secondary)' }}>Tu saldo ahora</span>
            <span className="inline-flex items-center gap-1.5 font-mono tabular text-2xl">
              <FierroIcon size={18} />
              {balance}
            </span>
          </div>
          <p className="mt-3 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
            Te regalamos <strong>15 fierros al registrarte</strong> y <strong>15 más cada mes</strong>{' '}
            automáticamente. Si exportas mucho, puedes comprar packs o suscribirte para ilimitado.
          </p>
        </>
      ),
    },
    {
      eyebrow: 'Recargas',
      title: 'Compra fierros o vuela sin límite',
      icon: <InfinityIcon size={22} strokeWidth={2} />,
      body: (
        <>
          <Bullet>50 fierros — S/. 14.90</Bullet>
          <Bullet>150 fierros — S/. 34.90 (mejor precio)</Bullet>
          <Bullet>
            <span style={{ color: 'var(--accent)' }}>Ilimitado</span> mensual — S/. 39
          </Bullet>
          <Bullet>
            <span style={{ color: 'var(--accent)' }}>Ilimitado</span> anual — S/. 390 (2 meses
            gratis)
          </Bullet>
          <p className="mt-4 text-[12px]" style={{ color: 'var(--text-muted)' }}>
            Pagos seguros por Culqi (tarjeta o Yape). Cancelas cuando quieras.
          </p>
        </>
      ),
    },
  ];

  const current = steps[Math.min(step, steps.length - 1)];
  const isLast = step >= steps.length - 1;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50"
          style={{ background: 'rgba(2,6,23,0.78)', backdropFilter: 'blur(10px)' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--radius-lg)] border shadow-2xl"
          style={{
            background: 'var(--surface-1)',
            borderColor: 'var(--border-default)',
            boxShadow: '0 30px 80px rgba(245,158,11,0.18), 0 0 0 1px var(--border-default)',
          }}
        >
          <Dialog.Close
            className="absolute right-3 top-3 z-10 rounded-[var(--radius-sm)] p-1.5 transition-colors hover:bg-[var(--surface-2)]"
            aria-label="Cerrar"
          >
            <X size={16} strokeWidth={1.8} />
          </Dialog.Close>

          {/* Decorative gradient header */}
          <div
            className="relative p-6 pb-4"
            style={{
              background:
                'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(245,158,11,0.02) 70%)',
            }}
          >
            <div className="mb-2 text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--accent)' }}>
              {current.eyebrow}
            </div>
            <div className="flex items-center gap-3">
              <span
                className="flex h-11 w-11 items-center justify-center rounded-[var(--radius)]"
                style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
              >
                {current.icon}
              </span>
              <Dialog.Title
                className="text-2xl"
                style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.02em' }}
              >
                {current.title}
              </Dialog.Title>
            </div>
          </div>

          <div className="px-6 pb-6 pt-2 text-[13.5px]" style={{ color: 'var(--text-primary)', lineHeight: 1.6 }}>
            {current.body}
          </div>

          {/* Pagination dots */}
          <div className="flex items-center justify-between border-t px-6 py-4" style={{ borderColor: 'var(--border-subtle)' }}>
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === step ? 24 : 8,
                    background: i === step ? 'var(--accent)' : 'var(--border-default)',
                  }}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setStep((s) => s - 1)}>
                  Atrás
                </Button>
              )}
              {!isLast && (
                <Button variant="primary" size="sm" onClick={() => setStep((s) => s + 1)}>
                  Siguiente <ArrowRight size={14} strokeWidth={2} />
                </Button>
              )}
              {isLast && (
                <>
                  <Button variant="ghost" size="sm" onClick={close}>
                    Empezar a usar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      close();
                      navigate('/app/billing');
                    }}
                  >
                    Ver planes <ArrowRight size={14} strokeWidth={2} />
                  </Button>
                </>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <CheckCircle2 size={14} strokeWidth={1.8} style={{ color: 'var(--accent)' }} />
      <span>{children}</span>
    </div>
  );
}
